"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function InsightsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/40 bg-card/30">
            <div className="h-48 w-full animate-pulse bg-muted/20" />
            <CardContent className="p-6">
              <div className="mb-4 h-5 w-24 animate-pulse rounded bg-muted/20" />
              <div className="mb-2 h-6 w-full animate-pulse rounded bg-muted/20" />
              <div className="mb-4 h-6 w-2/3 animate-pulse rounded bg-muted/20" />
              <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
              <div className="mt-2 h-4 w-4/5 animate-pulse rounded bg-muted/20" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-card/30 p-12 text-center">
        <p className="mb-4 text-muted-foreground">Unable to load market insights at this time.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-card/30 p-12 text-center">
        <p className="text-muted-foreground">No market insights available yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <div key={post.id} data-index={i} className="h-full">
          <Link to={post.link || "#"} className="group block h-full outline-none">
            <Card className="flex h-full flex-col overflow-hidden border-border/30 bg-card/40 backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:border-primary/40 hover:shadow-[0_8px_30px_-12px_var(--color-primary)] hover:bg-card/60">
              {}
              <div className="relative h-56 w-full overflow-hidden bg-muted/10">
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582830/img/generic_market_analysis.png"} alt={post.title} className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                {}
                <div className="absolute inset-0 bg-black/60 transition-colors duration-500" />
                {}
                <div className="absolute inset-0 bg-primary/0 mix-blend-overlay transition-colors duration-500 group-hover:bg-primary/50" />
                
                {}
                {post.categories && post.categories.length > 0 && <div className="absolute left-4 top-4 z-10">
                    <Badge variant="outline" className="border-primary/30 bg-background/80 text-primary backdrop-blur-md font-semibold tracking-wide uppercase text-[10px] px-3 py-1">
                      {post.categories[0].name}
                    </Badge>
                  </div>}
              </div>

              {}
              <CardContent className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex items-center gap-4 text-xs text-muted-foreground">
                  {post.date && <span className="flex items-center gap-1.5">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                      {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                    </span>}
                  <span className="flex items-center gap-1.5">
                    <Clock className="size-3.5" />
                    5 min read
                  </span>
                </div>

                <h3 className="mb-4 line-clamp-2 text-xl font-bold leading-tight text-foreground transition-colors duration-300 group-hover:text-primary">
                  {post.title}
                </h3>

                <p className="mb-6 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, '') : "Explore our latest institutional-grade market analysis and strategic insights designed for elite copytrading performance."}
                </p>

                <div className="mt-auto flex items-center text-sm font-semibold text-primary transition-all duration-300 group-hover:translate-x-1">
                  Read Insight <ArrowRight className="ml-2 size-4" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>)}
    </div>;
}
export default function EliteMarketInsights() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query({
    post_type: "post",
    ...wp_query_params
  });
  return <section id="elite-market-insights" className="dark bg-background text-foreground py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="mx-auto max-w-7xl relative z-10">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Elite Market <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Insights</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Institutional-grade analysis and strategic perspectives from our expert trading desk. Stay ahead of market movements.
            </p>
          </div>
          <Link to="#">
            <Button variant="outline" className="border-border/50 hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-all">
              View All Insights
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query}>
          <InsightsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}